// @givmo/fundraisers-module — naming a list of recipients the server gives as ids only (FCM-WEB25). A
// campaign's list, the list that binds it, and what an inviting campaign requires carry charity, Cause fund
// and collection ids and never a name (invariant 8 of the P2P design record), so each is named through the
// existing consumer read, whose names pass the response-boundary scrub (plan §FC-16.3). A recipient that
// read can't name keeps its kind's word, as a fundraiser's own chips do (`currentRecipients`).
import { useQueries } from '@tanstack/react-query';
import {
  charitiesGetCharityOptions,
  charityCollectionsGetCharityCollectionOptions,
  etfsGetEtfOptions,
} from '@givmo/api-client/query';
import type { RecipientItem, RecipientKind } from './recipients-decode';

export interface RecipientIds {
  charity_ids?: readonly string[] | null;
  etf_ids?: readonly string[] | null;
  collection_ids?: readonly string[] | null;
}

const FALLBACK: Record<RecipientKind, string> = {
  charity: 'Charity',
  etf: 'Fund',
  collection: 'Collection',
};

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/** The ids, in the server's order: charities, then Cause funds, then collections. */
export function recipientIdsOf(
  list: RecipientIds | null | undefined,
): { kind: RecipientKind; id: string }[] {
  if (!list) return [];
  return [
    ...(list.charity_ids ?? []).map((id) => ({ kind: 'charity' as const, id })),
    ...(list.etf_ids ?? []).map((id) => ({ kind: 'etf' as const, id })),
    ...(list.collection_ids ?? []).map((id) => ({ kind: 'collection' as const, id })),
  ];
}

/** Each listed recipient with its name, once every read has answered (`ready`). */
export function useRecipientNames(
  list: RecipientIds | null | undefined,
  enabled = true,
): { items: RecipientItem[]; ready: boolean } {
  const ids = recipientIdsOf(list);
  const results = useQueries({
    queries: ids.map(({ kind, id }) =>
      kind === 'charity'
        ? { ...charitiesGetCharityOptions({ path: { charity_id: id } }), enabled, retry: false }
        : kind === 'etf'
          ? { ...etfsGetEtfOptions({ path: { etf_id: id } }), enabled, retry: false }
          : {
              ...charityCollectionsGetCharityCollectionOptions({ path: { collection_id: id } }),
              enabled,
              retry: false,
            },
    ),
  });
  const items = ids.map(({ kind, id }, index) => {
    const data = results[index]?.data as
      | { name?: unknown; logo_url?: unknown; cover_image?: unknown }
      | undefined;
    return {
      kind,
      id,
      name: nonEmpty(data?.name) ?? FALLBACK[kind],
      imageUrl: nonEmpty(kind === 'collection' ? data?.cover_image : data?.logo_url),
    };
  });
  return { items, ready: results.every((result) => !result.isPending) };
}
